import React, { useState } from 'react';
import { Search, X } from 'lucide-react';
import { tagKey } from '../services/imagingTags';
import ShowMoreButton from './ShowMoreButton';
import './StudyListControls.css';

const COLLAPSED_TAG_CHIP_COUNT = 8;

function selectVisibleTagOptions(tagOptions, selectedTagKeys, expanded) {
    if (expanded || tagOptions.length <= COLLAPSED_TAG_CHIP_COUNT) return tagOptions;
    return tagOptions.filter((opt, index) => (
        index < COLLAPSED_TAG_CHIP_COUNT || selectedTagKeys.has(tagKey(opt.value))
    ));
}

function StudyListControls({
    search,
    onSearchChange,
    tagOptions,
    selectedTagKeys,
    onToggleTag,
    onClearFilters,
    hasActiveFilters,
    visibleCount,
    totalCount,
}) {
    const [tagsExpanded, setTagsExpanded] = useState(false);
    const visibleTagOptions = selectVisibleTagOptions(tagOptions, selectedTagKeys, tagsExpanded);
    const hiddenTagCount = tagOptions.length - visibleTagOptions.length;
    const canCollapseTags = tagsExpanded
        ? tagOptions.length > COLLAPSED_TAG_CHIP_COUNT
        : hiddenTagCount > 0;

    return (
        <div className="study-list-controls">
            <div className="study-list-controls-row">
                <div className="study-list-search">
                    <Search size={16} strokeWidth={2} />
                    <input
                        type="search"
                        value={search}
                        placeholder="Search studies, series, or tags"
                        aria-label="Search studies"
                        onChange={(e) => onSearchChange(e.target.value)}
                    />
                    {search && (
                        <button
                            type="button"
                            className="study-list-search-clear"
                            aria-label="Clear search"
                            onClick={() => onSearchChange('')}
                        >
                            <X size={14} strokeWidth={2} />
                        </button>
                    )}
                </div>
            </div>

            {tagOptions.length > 0 && (
                <div className="study-list-filter-group">
                    <div className="study-list-filter-chips">
                        {visibleTagOptions.map((opt) => {
                            const key = tagKey(opt.value);
                            const selected = selectedTagKeys.has(key);
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    className={`study-list-filter-chip${selected ? ' is-selected' : ''}`}
                                    aria-pressed={selected}
                                    onClick={() => onToggleTag(key)}
                                >
                                    <span>{opt.value}</span>
                                    <span className="study-list-filter-chip-count">{opt.count}</span>
                                </button>
                            );
                        })}
                        {canCollapseTags && (
                            <ShowMoreButton
                                className="study-list-filter-toggle"
                                expanded={tagsExpanded}
                                hiddenCount={hiddenTagCount}
                                onToggle={() => setTagsExpanded((open) => !open)}
                            />
                        )}
                    </div>
                </div>
            )}

            <div className="study-list-summary">
                <span>
                    {hasActiveFilters
                        ? `Showing ${visibleCount} of ${totalCount} ${totalCount === 1 ? 'study' : 'studies'}`
                        : `${totalCount} ${totalCount === 1 ? 'study' : 'studies'}`}
                </span>
                {hasActiveFilters && (
                    <button type="button" className="study-list-clear" onClick={onClearFilters}>
                        Clear filters
                    </button>
                )}
            </div>
        </div>
    );
}

export function StudyListNoResults({ onClearFilters }) {
    return (
        <div className="study-list-no-results">
            No studies match your search or filters.
            <button type="button" className="study-list-clear" onClick={onClearFilters}>
                Clear filters
            </button>
        </div>
    );
}

export default StudyListControls;
